import {
  Button,
  ButtonMenu,
  collapseColumn,
  column,
  Css,
  emptyCell,
  type FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableApi,
  GridTableLayout,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelectField,
  numericColumn,
  PageHeaderLayout,
  SelectField,
  simpleHeader,
  singleFilter,
  Tag,
  TagGroup,
  type TagGroupItem,
  TextField,
  Tooltip,
  TreeSelectField,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createProductOfferingUrl } from "src/RouteUrls";
import { priceCell } from "src/components";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { AggregateVersionStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  type ProductOfferingCostCalculatorPage_ComputeResultFragment,
  type ProductOfferingCostCalculatorPage_LineItemFragment,
  type ProductOfferingCostCalculatorPage_ProductOfferingFragment,
  useProductOfferingCostCalculatorMetaQuery,
  useProductOfferingCostCalculatorPage_CheckoutConfigLazyQuery,
  useProductOfferingCostCalculatorPage_ComputeLazyQuery,
} from "src/routes/developments/product-offerings/ProductOfferingCostCalculatorPage.generated";
import { type ProductOfferingCostCalculatorParams } from "src/routes/routesDef";
import { globalProductOfferingsRoute } from "src/routing/domains/librariesPaths";
import { fail, queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";

export function ProductOfferingCostCalculatorPage() {
  const { productOfferingId, rpavId } = useRouteParams<ProductOfferingCostCalculatorParams>();
  const query = useProductOfferingCostCalculatorMetaQuery({ variables: { id: productOfferingId, rpavId } });

  return queryResult(query, ({ productOffering }) => <DataView productOffering={productOffering} rpavId={rpavId} />);
}

type OptionGroup = ProductOfferingCostCalculatorPage_ProductOfferingFragment["optionGroups"][number];
type RpoOption = OptionGroup["options"][number];
type LineItemOption = ProductOfferingCostCalculatorPage_LineItemFragment["options"][number];
type OptionWithGroupOrder = { id: string; displayName: string; groupOrder: number };
/** A disabled option paired with an explicit reason (the missing prerequisites) for why it's disabled. */
type DisabledOption = { value: string; reason: string };
/** Which selector an option belongs to, i.e. a design-package single-select vs the add-ons multi-select. */
type OptionTier = "design" | "structural" | "personalization";
type CurrentVersion = ProductOfferingCostCalculatorPage_ProductOfferingFragment["version"];
type AggregateVersion = ProductOfferingCostCalculatorPage_ProductOfferingFragment["aggregateVersions"][number];

type Totals = { totalCostInCents: number; previousTotalCostInCents?: number; differenceTotalCostInCents?: number };

type GroupRowData = {
  id: string;
  displayName: string;
  totalCostInCents: number;
  previousTotalCostInCents?: number;
  differenceTotalCostInCents?: number;
};

/** A tliv with context from its parent ProtoProjectItem, for grouping. */
type TlivEntry = Omit<ProductOfferingCostCalculatorPage_LineItemFragment, "totalCostInCents"> & {
  rowId: string;
  // Previous-only comparison rows intentionally leave this undefined so the Current column renders as blank.
  totalCostInCents?: number;
  costCodeId: string;
  costCodeName: string;
  tradePartnerId: string;
  tradePartnerName: string;
  /** The last option (by optionGroup.order), used for "Group By: Option". */
  lastOption?: OptionWithGroupOrder;
  previousTotalCostInCents?: number;
  differenceTotalCostInCents?: number;
};

type Row =
  | { kind: "header" }
  | { kind: "totals"; data: Totals }
  | { kind: "group"; data: GroupRowData }
  | { kind: "tliv"; data: TlivEntry };

type GroupBy = "costCode" | "tradePartner" | "option" | "none";
const groupByOptions: { id: GroupBy; name: string }[] = [
  { id: "costCode", name: "Cost Code" },
  { id: "tradePartner", name: "Trade Partner" },
  { id: "option", name: "Option" },
  { id: "none", name: "None" },
];

/** The lone "Group By" control shown in the GridTableLayout's collapsible filter panel. */
type CostCalculatorFilter = { groupBy?: GroupBy };

type DataViewProps = { productOffering: ProductOfferingCostCalculatorPage_ProductOfferingFragment; rpavId: string };

/** Renders the page chrome (header + version selectors) and the option config / cost breakdown body. */
function DataView(props: DataViewProps) {
  const { productOffering, rpavId } = props;
  const [previousVersionId, setPreviousVersionId] = usePreviousVersionIdParam();
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModal();
  const api = useGridTableApi<Row>();
  const filterDefs = useMemo<FilterDefs<CostCalculatorFilter>>(
    () => ({
      groupBy: singleFilter({
        label: "Group By",
        options: groupByOptions,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
        defaultValue: "costCode",
      }),
    }),
    [],
  );
  const layoutState = useGridTableLayoutState<CostCalculatorFilter>({
    persistedFilter: { filterDefs, storageKey: "productOfferingCostCalculatorFilter" },
    search: "client",
  });
  const groupBy = layoutState.filter.groupBy ?? "costCode";

  const currentVersion = productOffering.version;
  const previousVersion = useMemo(
    () => productOffering.aggregateVersions.find((version) => version.id === previousVersionId),
    [productOffering.aggregateVersions, previousVersionId],
  );

  // Parse initial option IDs from the `?options=1,2,3` query param (only on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialOptionIds = useMemo(() => parseOptionsParam(location.search), []);
  // The applied config drives the compute + cost table; the draft selection lives in the Configure Options modal.
  const [appliedOptionIds, setAppliedOptionIds] = useState<string[]>(initialOptionIds);

  // Sync applied options back to the `?options=` query param
  const syncOptionsToUrl = useCallback(
    (ids: string[]) => {
      const params = new URLSearchParams(location.search);
      if (ids.isEmpty) {
        params.delete("options");
      } else {
        // I.e. ["rpo:1", "rpo:2"] → "1-2"
        params.set("options", ids.map((id) => id.replace("rpo:", "")).join("-"));
      }
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
    },
    [navigate, location.pathname, location.search],
  );

  useEffect(() => {
    syncOptionsToUrl(appliedOptionIds);
  }, [appliedOptionIds, syncOptionsToUrl]);

  const [runCurrentCompute, query] = useProductOfferingCostCalculatorPage_ComputeLazyQuery({ fetchPolicy: "no-cache" });
  const [runPreviousCompute, previousQuery] = useProductOfferingCostCalculatorPage_ComputeLazyQuery({
    fetchPolicy: "no-cache",
  });

  const applySelections = useCallback(
    async (optionIds: string[]) => {
      if (optionIds.isEmpty) {
        setAppliedOptionIds([]);
        return;
      }
      const { data } = await runCurrentCompute({
        variables: { readyPlanId: productOffering.id, readyPlanOptionIds: optionIds, versionId: currentVersion.id },
      });
      // The backend resolves the full config (e.g. adds dependent options); reopening the modal reflects it.
      if (data) setAppliedOptionIds(data.computeReadyPlanCost.readyPlanOptions.map((option) => option.id));
    },
    [currentVersion.id, productOffering.id, runCurrentCompute],
  );

  const didAutoApplyInitialOptions = useRef(false);

  useEffect(() => {
    if (didAutoApplyInitialOptions.current || initialOptionIds.isEmpty) return;
    didAutoApplyInitialOptions.current = true;
    void applySelections(initialOptionIds);
  }, [applySelections, initialOptionIds]);

  useEffect(() => {
    if (!previousVersionId || appliedOptionIds.isEmpty) return;
    void runPreviousCompute({
      variables: {
        readyPlanId: productOffering.id,
        readyPlanOptionIds: appliedOptionIds,
        versionId: previousVersionId,
      },
    });
  }, [appliedOptionIds, previousVersionId, productOffering.id, runPreviousCompute]);

  // Refetch when contingency markup changes so cost updates without other interaction
  useEffect(() => {
    if (appliedOptionIds.nonEmpty) {
      void applySelections(appliedOptionIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productOffering.contingencyMarkupPercentage]);

  const skip = appliedOptionIds.isEmpty;
  const currentComputeReadyPlanCost = skip
    ? undefined
    : (query.data?.computeReadyPlanCost ?? query.previousData?.computeReadyPlanCost);
  const configurationErrors = currentComputeReadyPlanCost?.configurationErrors;
  const resetSelections = useCallback(() => setAppliedOptionIds([]), []);
  // Tags summarizing the applied config, shown with a trailing "Edit" control.
  const configTags = useMemo(
    () => summarizeAppliedConfig(productOffering.optionGroups, appliedOptionIds),
    [productOffering.optionGroups, appliedOptionIds],
  );

  const openConfigureOptions = useCallback(
    () =>
      openModal({
        content: (
          <ConfigureOptionsModal
            productOffering={productOffering}
            initialOptionIds={appliedOptionIds}
            onApply={(ids) => void applySelections(ids)}
            onReset={resetSelections}
          />
        ),
      }),
    [appliedOptionIds, applySelections, openModal, productOffering, resetSelections],
  );

  const csvPrefixRows: string[][] = [
    ["Plan", productOffering.displayName],
    // TODO: add computeProgramData back once the query field is uncommented
    // ["Sellable Sqft", String(query.data?.computeProgramData?.sellableSqft ?? "N/A")],
    ...(previousVersion
      ? [
          ["Previous Version", getAggregateVersionLabel(previousVersion)],
          ["Current Version", currentVersion.displayVersion],
        ]
      : [["Version", currentVersion.displayVersion]]),
  ];
  const csvFileName =
    [
      productOffering.displayName.replaceAll(".", "_"),
      ...(previousVersion
        ? [getAggregateVersionLabel(previousVersion), currentVersion.displayVersion]
        : [currentVersion.displayVersion]),
    ].join("_") + ".csv";

  return (
    <PageHeaderLayout
      pageHeader={{
        title: `${productOffering.displayName} — Cost Calculator`,
        breadcrumbs: {
          breadcrumbs: [
            { label: "Product Offerings", href: globalProductOfferingsRoute.url() },
            {
              label: productOffering.displayName,
              href: createProductOfferingUrl(
                productOffering.id,
                rpavId,
                productOffering.development?.id,
                productOffering.project?.id,
              ),
            },
          ],
        },
        rightSlot: (
          <div css={Css.df.aic.gap2.$}>
            {productOffering.aggregateVersions.length > 1 && (
              <ReadyPlanVersionHeader
                readyPlan={productOffering}
                versionId={previousVersionId}
                label="Previous Version"
                onSelect={setPreviousVersionId}
                allowUnset={true}
              />
            )}
            <ReadyPlanVersionHeader
              readyPlan={productOffering}
              versionId={productOffering.version.id}
              aggregateCheckoutId={productOffering.aggregateCheckout?.id}
            />
            <ButtonMenu
              trigger={{ icon: "verticalDots" }}
              placement="right"
              items={[
                { label: "Download CSV", onClick: () => api.downloadToCsv(csvFileName) },
                {
                  label: "Load Checkout",
                  onClick: () =>
                    openModal({ content: <LoadCheckoutModal onLoad={(ids) => void applySelections(ids)} /> }),
                },
              ]}
            />
          </div>
        ),
      }}
    >
      <div css={Css.df.gap1.m3.$}>
        {configTags.nonEmpty ? (
          <TagGroup tags={configTags} />
        ) : (
          <span css={Css.sm.gray700.$}>No options configured</span>
        )}
        <Button variant="text" label="Edit" onClick={openConfigureOptions} />
        {configurationErrors && configurationErrors.nonEmpty && (
          <Tooltip
            title={
              <ul css={Css.pl2.$}>
                {configurationErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            }
          >
            <Tag text={`${configurationErrors.length} Configuration Errors`} type="warning" />
          </Tooltip>
        )}
      </div>
      {!skip &&
        queryResult(query, ({ computeReadyPlanCost }) =>
          previousVersionId ? (
            queryResult(previousQuery, ({ computeReadyPlanCost: previousComputeReadyPlanCost }) => (
              <CostView
                api={api}
                layoutState={layoutState}
                currentResult={computeReadyPlanCost}
                previousResult={previousComputeReadyPlanCost}
                currentVersion={currentVersion}
                previousVersion={previousVersion}
                csvPrefixRows={csvPrefixRows}
                groupBy={groupBy}
              />
            ))
          ) : (
            <CostView
              api={api}
              layoutState={layoutState}
              currentResult={computeReadyPlanCost}
              currentVersion={currentVersion}
              csvPrefixRows={csvPrefixRows}
              groupBy={groupBy}
            />
          ),
        )}
    </PageHeaderLayout>
  );
}

type ConfigureOptionsModalProps = {
  productOffering: ProductOfferingCostCalculatorPage_ProductOfferingFragment;
  initialOptionIds: string[];
  onApply: (optionIds: string[]) => void;
  onReset: () => void;
};

/**
 * Modal that holds the draft option configuration (one single-select per design package group, plus
 * structural add-ons and personalization). Apply commits the draft to the cost table and closes.
 */
function ConfigureOptionsModal(props: ConfigureOptionsModalProps) {
  const { productOffering, initialOptionIds, onApply, onReset } = props;
  const { closeModal } = useModal();

  const { designPackageGroups, structuralAndAddonOptions, personalizationGroups, tierByOptionId } = useMemo(
    () => categorizeOptionGroups(productOffering.optionGroups),
    [productOffering.optionGroups],
  );

  // Draft state: one selected RPO ID per design-package RPOG (single-select).
  // Hydrate from the applied config if present, otherwise default to the first eligible option per group.
  const [designSelections, setDesignSelections] = useState<Record<string, string | undefined>>(() => {
    const selections: Record<string, string | undefined> = {};
    for (const id of initialOptionIds) {
      const info = tierByOptionId.get(id);
      if (info?.tier === "design") selections[info.groupId] = id;
    }
    if (initialOptionIds.isEmpty) {
      for (const group of designPackageGroups) {
        if (!selections[group.id]) {
          const firstEligible = group.options.find(
            (option) => option.active && hasSatisfiedOptionPrerequisites(option, Object.values(selections).compact()),
          );
          const firstActive = firstEligible ?? group.options.find((o) => o.active);
          if (firstActive) selections[group.id] = firstActive.id;
        }
      }
    }
    return selections;
  });
  // Draft state: selected RPO IDs for structural & add-ons (multi-select) and personalization (tree).
  const [structuralIds, setStructuralIds] = useState<string[]>(() =>
    initialOptionIds.filter((id) => tierByOptionId.get(id)?.tier === "structural"),
  );
  const [personalizationIds, setPersonalizationIds] = useState<string[]>(() =>
    initialOptionIds.filter((id) => tierByOptionId.get(id)?.tier === "personalization"),
  );

  const selectedOptionIds = useMemo(
    () => [...Object.values(designSelections), ...structuralIds, ...personalizationIds].compact(),
    [designSelections, structuralIds, personalizationIds],
  );
  const disabledOptions = useMemo(
    () => getDisabledOptions(productOffering.optionGroups, selectedOptionIds),
    [productOffering.optionGroups, selectedOptionIds],
  );

  return (
    <>
      <ModalHeader>Configure Options</ModalHeader>
      <ModalBody>
        <OptionSelectors
          designPackageGroups={designPackageGroups}
          designSelections={designSelections}
          onDesignChange={(groupId, value) => setDesignSelections((prev) => ({ ...prev, [groupId]: value }))}
          structuralAndAddonOptions={structuralAndAddonOptions}
          structuralIds={structuralIds}
          onStructuralChange={setStructuralIds}
          personalizationGroups={personalizationGroups}
          personalizationIds={personalizationIds}
          onPersonalizationChange={setPersonalizationIds}
          disabledOptions={disabledOptions}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          variant="tertiary"
          label="Reset"
          disabled={selectedOptionIds.isEmpty}
          onClick={() => {
            setDesignSelections({});
            setStructuralIds([]);
            setPersonalizationIds([]);
            onReset();
          }}
        />
        <Button
          label="Apply"
          disabled={selectedOptionIds.isEmpty}
          onClick={() => {
            onApply(selectedOptionIds);
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type OptionSelectorsProps = {
  designPackageGroups: OptionGroup[];
  designSelections: Record<string, string | undefined>;
  onDesignChange: (groupId: string, value: string | undefined) => void;
  structuralAndAddonOptions: RpoOption[];
  structuralIds: string[];
  onStructuralChange: (ids: string[]) => void;
  personalizationGroups: { id: string; name: string; children: { id: string; name: string }[] }[];
  personalizationIds: string[];
  onPersonalizationChange: (ids: string[]) => void;
  disabledOptions: DisabledOption[];
};

/** Renders the option selection controls (design packages, structural add-ons, personalization). */
function OptionSelectors(props: OptionSelectorsProps) {
  const {
    designPackageGroups,
    designSelections,
    onDesignChange,
    structuralAndAddonOptions,
    structuralIds,
    onStructuralChange,
    personalizationIds,
    onPersonalizationChange,
    personalizationGroups,
    disabledOptions,
  } = props;

  return (
    <div css={Css.df.fdc.gap2.$}>
      {/* Row 1: Design package RPOGs — one single-select per group */}
      <div css={Css.df.gap2.flexWrap("wrap").$}>
        {designPackageGroups.map((group) => (
          <SelectField
            key={group.id}
            label={group.name}
            value={designSelections[group.id]}
            onSelect={(value) => onDesignChange(group.id, value)}
            options={group.options.filter((o) => o.active).sortBy((o) => o.displayName)}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.displayName}
            nothingSelectedText="—"
            disabledOptions={disabledOptions}
            sizeToContent
          />
        ))}
      </div>
      {/* Row 2: Structural & Add-Ons + Personalization multi-selects */}
      <div css={Css.df.gap2.flexWrap("wrap").$}>
        {structuralAndAddonOptions.nonEmpty && (
          <MultiSelectField
            label="Structural & Add-Ons"
            values={structuralIds}
            onSelect={onStructuralChange}
            options={structuralAndAddonOptions}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.displayName}
            nothingSelectedText="—"
            disabledOptions={disabledOptions}
            sizeToContent
          />
        )}
        {personalizationGroups.nonEmpty && (
          <TreeSelectField
            label="Personalization"
            values={personalizationIds}
            onSelect={(response) => onPersonalizationChange(response.leaf.values)}
            options={personalizationGroups}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.name}
            groupOptions={personalizationGroups.map((g) => g.id)}
            nothingSelectedText="—"
            sizeToContent
            defaultCollapsed
            // TreeSelectField only supports disabling by value, so we drop the reason here.
            disabledOptions={disabledOptions.map((o) => o.value)}
          />
        )}
      </div>
    </div>
  );
}

type CostViewProps = {
  api: GridTableApi<Row>;
  layoutState: ReturnType<typeof useGridTableLayoutState<CostCalculatorFilter>>;
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment;
  previousResult?: ProductOfferingCostCalculatorPage_ComputeResultFragment;
  currentVersion: CurrentVersion;
  previousVersion?: AggregateVersion;
  csvPrefixRows: string[][];
  groupBy: GroupBy;
};

/** Renders the cost breakdown table with the modern GridTableLayout (collapsible search + Group By filter). */
function CostView(props: CostViewProps) {
  const { api, layoutState, currentResult, previousResult, currentVersion, previousVersion, csvPrefixRows, groupBy } =
    props;
  const columns = useMemo(() => createColumns(currentVersion, previousVersion), [currentVersion, previousVersion]);
  const optionById = useMemo(() => createOptionById(currentResult, previousResult), [currentResult, previousResult]);
  const rows = useMemo(
    () =>
      previousResult
        ? createComparisonRows(currentResult, previousResult, groupBy, optionById)
        : createRows(currentResult, groupBy, optionById),
    [currentResult, groupBy, optionById, previousResult],
  );
  return (
    <GridTableLayout
      layoutState={layoutState}
      tableProps={{ api, columns, rows, csvPrefixRows, sorting: { on: "client" }, as: "virtual" }}
    />
  );
}

/** Creates the grid columns. */
function createColumns(currentVersion: CurrentVersion, previousVersion?: AggregateVersion): GridColumn<Row>[] {
  const groupColumn = column<Row>({
    id: "group",
    name: "Group",
    header: "Group",
    group: ({ displayName }) => displayName,
    tliv: emptyCell,
    totals: emptyCell,
    showIn: "csv",
  });
  const nameColumn = column<Row>({
    id: "name",
    name: "Name",
    header: "Name",
    group: ({ id, displayName }) => ({
      content: () => (
        <Tooltip title={id} placement="left">
          {displayName}
        </Tooltip>
      ),
      value: displayName,
    }),
    tliv: (li) => ({
      content: () => (
        <Tooltip title={li.id} placement="left">
          {li.displayName}
        </Tooltip>
      ),
      value: li.displayName,
    }),
    totals: emptyCell,
    w: "450px",
  });
  const optionsColumn = column<Row>({
    id: "options",
    name: "Options",
    header: "Options",
    group: emptyCell,
    tliv: (li) => ({
      content: () => (
        <div css={Css.df.fdc.$}>
          {li.options.map((o) => (
            <div key={o.id}>{o.code}</div>
          ))}
        </div>
      ),
      value: li.options.map((o) => o.code).join(", "),
    }),
    totals: emptyCell,
    w: "250px",
  });
  const qtyColumn = numericColumn<Row>({
    id: "qty",
    name: "Qty",
    header: "Qty",
    group: emptyCell,
    tliv: (li) => li.quantity ?? emptyCell,
    totals: emptyCell,
    w: "80px",
  });
  const unitColumn = column<Row>({
    id: "unit",
    name: "Unit",
    header: "Unit",
    group: emptyCell,
    tliv: (li) => li.unitOfMeasure?.abbreviation ?? emptyCell,
    totals: emptyCell,
    w: "70px",
  });
  const unitCostColumn = numericColumn<Row>({
    id: "unitCost",
    name: "Unit Cost",
    header: "Unit Cost",
    group: emptyCell,
    tliv: (li) => priceCell({ valueInCents: li.unitCostInCents }),
    totals: emptyCell,
    w: "120px",
  });
  const costSourceColumn = column<Row>({
    id: "costSource",
    name: "Cost Source",
    header: "Cost Source",
    group: emptyCell,
    tliv: (li) => {
      const revision = li.preferredBidContractLineItem?.revision;
      if (!revision) return emptyCell;
      return {
        content: () => <Link to={revision.blueprintUrl.path}>{revision.displayName}</Link>,
        value: revision.displayName,
      };
    },
    totals: emptyCell,
    w: "400px",
  });
  const comparisonMode = !!previousVersion;
  const costColumns = comparisonMode
    ? [
        numericColumn<Row>({
          id: "previous",
          name: "Previous",
          header: `Previous ${getAggregateVersionLabel(previousVersion)}`,
          group: (data) => priceCell({ valueInCents: data.previousTotalCostInCents }),
          tliv: (li) => priceCell({ valueInCents: li.previousTotalCostInCents }),
          totals: (totals) => priceCell({ valueInCents: totals.previousTotalCostInCents }),
          w: "150px",
        }),
        numericColumn<Row>({
          id: "current",
          name: "Current",
          header: `Current ${currentVersion.displayVersion}`,
          group: (data) => priceCell({ valueInCents: data.totalCostInCents }),
          tliv: (li) => priceCell({ valueInCents: li.totalCostInCents }),
          totals: (totals) => priceCell({ valueInCents: totals.totalCostInCents }),
          w: "150px",
        }),
        numericColumn<Row>({
          id: "diff",
          name: "Difference",
          header: "Difference",
          group: (data) => priceCell({ valueInCents: data.differenceTotalCostInCents }),
          tliv: (li) => priceCell({ valueInCents: li.differenceTotalCostInCents }),
          totals: (totals) => priceCell({ valueInCents: totals.differenceTotalCostInCents }),
          w: "150px",
        }),
      ]
    : [
        numericColumn<Row>({
          id: "totalCost",
          name: "Total Cost",
          header: "Total Cost",
          group: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
          tliv: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
          totals: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
          w: "120px",
        }),
      ];

  return [
    collapseColumn<Row>({ totals: emptyCell }),
    groupColumn,
    nameColumn,
    optionsColumn,
    qtyColumn,
    unitColumn,
    unitCostColumn,
    costSourceColumn,
    ...costColumns,
  ];
}

/** Creates the grid rows for a single version, optionally grouped by cost code, trade partner, or option. */
function createRows(
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  groupBy: GroupBy,
  optionById: Map<string, OptionWithGroupOrder>,
): GridDataRow<Row>[] {
  const entries = createTlivEntries(currentResult, optionById);
  const totals: Totals = { totalCostInCents: currentResult.totalCostInCents };
  return buildRows(entries, totals, groupBy);
}

function createComparisonRows(
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  previousResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  groupBy: GroupBy,
  optionById: Map<string, OptionWithGroupOrder>,
): GridDataRow<Row>[] {
  const previousEntries = createTlivEntries(previousResult, optionById);
  const currentEntries = createTlivEntries(currentResult, optionById);
  const entries = mergeTlivEntries(previousEntries, currentEntries);
  const totals: Totals = {
    totalCostInCents: currentResult.totalCostInCents,
    previousTotalCostInCents: previousResult.totalCostInCents,
    differenceTotalCostInCents: currentResult.totalCostInCents - previousResult.totalCostInCents,
  };
  return buildRows(entries, totals, groupBy);
}

function buildRows(entries: TlivEntry[], totals: Totals, groupBy: GroupBy): GridDataRow<Row>[] {
  if (groupBy === "none") {
    return [{ kind: "totals", id: "totals", data: totals }, simpleHeader, ...toTlivRows(entries)];
  }
  // Group entries by the selected dimension
  const grouped = entries.groupBy((entry) => entryGroupKey(entry, groupBy));
  const groupEntries = Object.entries(grouped);
  const groupRows: GridDataRow<Row>[] = groupEntries.map(
    ([groupId, items]) =>
      ({
        kind: "group" as const,
        id: groupId,
        fixedSort: groupBy === "option" && groupId === "base" ? "first" : undefined,
        data: {
          id: groupId,
          displayName: entryGroupName(items[0], groupBy),
          totalCostInCents: items.map((entry) => entry.totalCostInCents).sum(),
          previousTotalCostInCents: items.map((entry) => entry.previousTotalCostInCents).sum(),
          differenceTotalCostInCents: items.map((entry) => entry.differenceTotalCostInCents).sum(),
        },
        children: toTlivRows(items),
      }) satisfies GridDataRow<Row>,
  );
  return [{ kind: "totals", id: "totals", data: totals }, simpleHeader, ...groupRows];
}

/** Glue together curr/previous options to get options active in both. */
function createOptionById(
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  previousResult?: ProductOfferingCostCalculatorPage_ComputeResultFragment,
): Map<string, OptionWithGroupOrder> {
  return [currentResult, previousResult]
    .compact()
    .flatMap((result) =>
      result.readyPlan.optionGroups.flatMap((rpog) =>
        rpog.options.map((rpo) => {
          return { ...rpo, groupOrder: rpog.order };
        }),
      ),
    )
    .uniqueBy((rpo) => rpo.id)
    .keyByObject((rpo) => rpo.id);
}

function createTlivEntries(
  result: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  optionById: Map<string, OptionWithGroupOrder>,
): TlivEntry[] {
  // Immediately flatMap proto project items → tliv entries with parent context
  const entries: TlivEntry[] = result.protoProjectItems.flatMap((pi, piIdx) => {
    const lineItems = [pi.itemTemplateItem, ...pi.modifyingTemplateItems].compact();
    const tp = pi.preferredPotentialBidContractLineItem?.bidContractLineItem?.revision.bidContract.tradePartner;
    /** Shared grouping dimensions from the parent ProtoProjectItem. */
    function dims(opts: readonly LineItemOption[]) {
      const lastOption = opts
        .map(
          (option) =>
            optionById.get(option.id) ??
              // Even if we union "previous version options" + "current version options" the TLIs themselves
              // can still be tagged with active=false options, and so lead to a missing option error
              { id: option.id, displayName: `Archived Option ${option.id}`, groupOrder: Number.MAX_SAFE_INTEGER },
        )
        .sortBy((option) => option.groupOrder).last;
      return {
        costCodeId: pi.item.costCode.id,
        costCodeName: pi.item.costCode.displayName,
        tradePartnerId: tp?.id ?? "unassigned",
        tradePartnerName: tp?.name ?? "Homebound Internal Estimate",
        lastOption,
      };
    }
    if (lineItems.nonEmpty) {
      return lineItems.map((li) => ({ ...li, rowId: li.id, ...dims(li.options) }) as TlivEntry);
    }
    // Synthetic entry for proto items with no backing line items (e.g. contingency)
    return [
      {
        id: "tli:0",
        rowId: `pi-${piIdx}-tli:0`,
        displayName: pi.name,
        options: [],
        preferredBidContractLineItem: undefined,
        quantity: pi.quantity,
        unitCostInCents: 0,
        unitOfMeasure: pi.unitOfMeasure,
        totalCostInCents: pi.totalCostInCents,
        ...dims([]),
      } as TlivEntry,
    ];
  });
  return entries;
}

/** Converts tliv entries into GridDataRows. */
function toTlivRows(entries: TlivEntry[]): GridDataRow<Row>[] {
  return entries.map((e) => ({ kind: "tliv" as const, id: e.rowId, data: e }));
}

function mergeTlivEntries(previousEntries: TlivEntry[], currentEntries: TlivEntry[]): TlivEntry[] {
  const previousById = previousEntries.keyBy((entry) => entry.id);
  const currentById = currentEntries.keyBy((entry) => entry.id);
  const orderedIds = [...Object.keys(previousById), ...Object.keys(currentById)].unique();
  return orderedIds.map((id) => {
    const prevEntry = previousById[id];
    const currEntry = currentById[id];
    const baseEntry = currEntry ?? prevEntry ?? fail("Expected either a current or previous cost entry");
    const previousTotalCostInCents = prevEntry?.totalCostInCents;
    const currentTotalCostInCents = currEntry?.totalCostInCents;
    return {
      ...baseEntry,
      rowId: currEntry?.rowId ?? prevEntry?.rowId ?? baseEntry.rowId,
      totalCostInCents: currentTotalCostInCents,
      previousTotalCostInCents,
      differenceTotalCostInCents: (currentTotalCostInCents ?? 0) - (previousTotalCostInCents ?? 0),
    };
  });
}

/** Returns the group key for a tliv entry based on the active groupBy mode. */
function entryGroupKey(entry: TlivEntry, groupBy: GroupBy): string {
  if (groupBy === "costCode") return entry.costCodeId;
  if (groupBy === "tradePartner") return entry.tradePartnerId;
  return entry.lastOption?.id ?? "base";
}

/** Returns the group display name for a tliv entry based on the active groupBy mode. */
function entryGroupName(entry: TlivEntry, groupBy: GroupBy): string {
  if (groupBy === "costCode") return entry.costCodeName;
  if (groupBy === "tradePartner") return entry.tradePartnerName;
  return entry.lastOption?.displayName ?? "Base House";
}

/** Return the label for putting in the csv. */
function getAggregateVersionLabel(
  version: ProductOfferingCostCalculatorPage_ProductOfferingFragment["aggregateVersions"][number],
) {
  return version.status.code === AggregateVersionStatus.Draft
    ? `Draft of v${version.version ?? "?"}`
    : `v${version.version ?? "?"}`;
}

function usePreviousVersionIdParam() {
  const [{ prev }, setQueryParams] = useQueryParams({ prev: StringParam });
  const setPreviousVersionId = useCallback(
    (versionId: string | undefined) => {
      setQueryParams({ prev: versionId?.replace(":", "_") }, "replaceIn");
    },
    [setQueryParams],
  );
  return [prev?.replace("_", ":"), setPreviousVersionId] as const;
}

type LoadCheckoutModalProps = { onLoad: (ids: string[]) => void };

/** Modal that applies the selected options from a checkout config by ID. */
function LoadCheckoutModal(props: LoadCheckoutModalProps) {
  const { onLoad } = props;
  const { closeModal } = useModal();
  const [configId, setConfigId] = useState<string | undefined>();
  const [fetchCheckout, { loading, error }] = useProductOfferingCostCalculatorPage_CheckoutConfigLazyQuery();

  async function handleLoad() {
    if (!configId) return;
    const { data } = await fetchCheckout({ variables: { id: configId } });
    if (data) {
      onLoad(data.checkoutConfig.options.map((o) => o.id));
      closeModal();
    }
  }

  return (
    <>
      <ModalHeader>Load Checkout Config</ModalHeader>
      <ModalBody>
        <TextField label="Checkout Config ID" value={configId} onChange={setConfigId} placeholder="ccfg:1234" />
        {error && <div css={Css.sm.red600.mt1.$}>Failed to load: {error.message}</div>}
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" label="Cancel" onClick={closeModal} />
        <Button label="Load" disabled={!configId || loading} onClick={handleLoad} />
      </ModalFooter>
    </>
  );
}

function getDisabledOptions(optionGroups: OptionGroup[], selectedOptionIds: string[]): DisabledOption[] {
  const optionNameById = new Map(optionGroups.flatMap((group) => group.options).map((o) => [o.id, o.displayName]));
  const selectedOptionIdsSet = new Set(selectedOptionIds);
  return optionGroups.flatMap((group) =>
    group.options
      .filter((option) => option.active)
      .map((option) => {
        const missingPrereqGroups = getUnsatisfiedPrerequisites(option, selectedOptionIdsSet);
        if (missingPrereqGroups.isEmpty) return undefined;
        // Each group is satisfied by any one of its prereqs (OR); every group must be satisfied (AND).
        // Multi-option groups are wrapped in parens to disambiguate, e.g. "(Modern or Contemporary) and Flat".
        const reason =
          "Requires " +
          missingPrereqGroups
            .map((prereqIds) => {
              const names = prereqIds.map((id) => optionNameById.get(id) ?? id);
              return names.length > 1 ? `(${names.join(" or ")})` : names[0];
            })
            .join(" and ");
        return { value: option.id, reason };
      })
      .compact(),
  );
}

/**
 * Returns the unsatisfied prerequisite groups for an option, where each entry is the list of option
 * IDs (any one of which satisfies that group). An empty result means all prerequisites are satisfied.
 */
function getUnsatisfiedPrerequisites(option: RpoOption, selectedOptionIds: Set<string>): string[][] {
  if (option.optionPrerequisites.isEmpty) return [];
  return option.optionPrerequisites
    .groupBy((prereq) => prereq.optionGroup.id)
    .toValues()
    .filter((prereqsInGroup) => !prereqsInGroup.some((prereq) => selectedOptionIds.has(prereq.id)))
    .map((prereqsInGroup) => prereqsInGroup.map((prereq) => prereq.id));
}

function hasSatisfiedOptionPrerequisites(option: RpoOption, selectedOptionIds: string[]): boolean {
  return getUnsatisfiedPrerequisites(option, new Set(selectedOptionIds)).isEmpty;
}

/**
 * Summarizes the applied config as tags. Package + structural options each get their own tag; the many
 * personalization options collapse into one count tag. I.e. "E1 - Craftsman", "Add Garage Bay", "28 personalization options".
 */
function summarizeAppliedConfig(optionGroups: OptionGroup[], appliedOptionIds: string[]): TagGroupItem[] {
  const { tierByOptionId } = categorizeOptionGroups(optionGroups);
  const appliedIds = new Set(appliedOptionIds);
  const designPackageTags: TagGroupItem[] = [];
  const structuralTags: TagGroupItem[] = [];
  let personalizationCount = 0;

  // One pass in group order (Elevation, Exterior Scheme, ...), bucketing each applied option by its tier.
  for (const group of optionGroups.sortBy((g) => g.order)) {
    for (const option of group.options) {
      if (!appliedIds.has(option.id)) continue;
      const { tier } = tierByOptionId.get(option.id) ?? fail(`Expected a tier for option ${option.id}`);
      if (tier === "design") designPackageTags.push({ text: option.displayName });
      else if (tier === "structural") structuralTags.push({ text: option.displayName });
      else personalizationCount += 1;
    }
  }

  return [
    ...designPackageTags,
    ...structuralTags,
    ...(personalizationCount > 0
      ? [{ text: `${personalizationCount} personalization option${personalizationCount === 1 ? "" : "s"}` }]
      : []),
  ];
}

/**
 * Splits option groups into the selector tiers, and indexes every option's tier + owning group.
 *
 * The tier rules live here only: design-package RPOGs (exterior/interior) each become one single-select,
 * and the remaining groups split by option type into structural/add-ons vs personalization.
 *
 * The selector data (`designPackageGroups` / `structuralAndAddonOptions` / `personalizationGroups`) only
 * includes active groups and active options, while `tierByOptionId` covers inactive ones too — an applied
 * (or `?options=`) id can name an option whose group was since deactivated, and it still needs to classify.
 */
function categorizeOptionGroups(optionGroups: OptionGroup[]) {
  // Only active groups get a selector; inactive ones are still classified into `tierByOptionId` below.
  const activeGroups = optionGroups.filter((g) => g.active);

  // Tier 1: Design package RPOGs — one single-select per group
  const designPackageGroups = activeGroups.filter(isDesignGroup).sortBy((g) => g.order);

  // Tiers 2 & 3: every other active group, i.e. the exact complement of the design groups.
  const nonDesignGroups = activeGroups.filter((g) => !isDesignGroup(g)).sortBy((g) => g.order);
  // Sorted by RPOG order, then by displayName within each group.
  const activeNonDesignOptions = nonDesignGroups.flatMap((g) =>
    g.options.filter((o) => o.active).sortBy((o) => o.displayName),
  );

  // I.e. "Add Bedroom", "Add Garage Bay", structural upgrades
  const structuralAndAddonOptions = activeNonDesignOptions.filter(isStructuralOption);

  // Personalization groups as a tree for TreeSelectField (groups → options)
  const personalizationGroups = nonDesignGroups
    .map((g) => ({
      id: g.id,
      name: g.name,
      children: g.options
        .filter((o) => o.active && !isStructuralOption(o))
        .sortBy((o) => o.displayName)
        .map((o) => ({ id: o.id, name: o.displayName })),
    }))
    .filter((g) => g.children.nonEmpty);

  // Every option in every group, active or not. I.e. rpo:1 → { groupId: "rpog:1", tier: "design" }
  const tierByOptionId = new Map<string, { groupId: string; tier: OptionTier }>();
  for (const group of optionGroups) {
    const isDesign = isDesignGroup(group);
    for (const option of group.options) {
      const tier: OptionTier = isDesign ? "design" : isStructuralOption(option) ? "structural" : "personalization";
      tierByOptionId.set(option.id, { groupId: group.id, tier });
    }
  }

  return { designPackageGroups, structuralAndAddonOptions, personalizationGroups, tierByOptionId };
}

/**
 * True for the design-package RPOGs rendered as single-selects, i.e. Elevation / Exterior Scheme.
 *
 * The schema's `forDesignPackages` is just a server-side alias for this same `interior || exterior` OR,
 * so don't re-test it alongside this — every group is either a design group or not.
 */
function isDesignGroup(group: OptionGroup): boolean {
  return group.forDesignExterior || group.forDesignInterior;
}

/** True for structural & add-on options, i.e. "Add Garage Bay". */
function isStructuralOption(option: RpoOption): boolean {
  // `isStructuralV2`/`isAddOnV2` are nullable in the schema, so coerce to a real boolean.
  return !!(option.type.isStructuralV2 || option.type.isAddOnV2);
}

/** Parses `?options=1,2,3` into `["rpo:1", "rpo:2", "rpo:3"]`. */
function parseOptionsParam(search: string): string[] {
  const raw = new URLSearchParams(search).get("options");
  if (!raw) return [];
  return raw.split("-").map((id) => `rpo:${id.trim()}`);
}
